import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

type AccentColorMode = 'theme' | 'default';

interface AccentColorModeContextType {
    accentColorMode: AccentColorMode;
    setAccentColorMode: (mode: AccentColorMode) => void;
}

const AccentColorModeContext = createContext<AccentColorModeContextType>({
    accentColorMode: 'theme',
    setAccentColorMode: () => { },
});

export const useAccentColorMode = () => useContext(AccentColorModeContext);

export const AccentColorModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [accentColorMode, setAccentColorMode] = useState<AccentColorMode>(() => {
        const saved = localStorage.getItem('accent-color-mode');
        return (saved as AccentColorMode) || 'theme';
    });

    useEffect(() => {
        localStorage.setItem('accent-color-mode', accentColorMode);
        document.documentElement.setAttribute('data-accent-color-mode', accentColorMode);
    }, [accentColorMode]);

    // Memoize context value to prevent unnecessary re-renders
    const value = useMemo(() => ({
        accentColorMode,
        setAccentColorMode
    }), [accentColorMode]);

    return (
        <AccentColorModeContext.Provider value={value}>
            {children}
        </AccentColorModeContext.Provider>
    );
};
